import { z } from 'zod';
import { BankCsvMapping } from './bank-csv';

// Contracts of the Ledger module, shared by the API, the web app and (later) agent tools.
// Amounts are integer cents.

export const IsoDate = z.iso.date();
export const Id = z.uuid();
export const Cents = z.int().min(0).max(1_000_000_000_00);

export const FundKind = z.enum(['operating', 'contingency', 'self_insurance', 'special']);
export const AccountType = z.enum(['asset', 'liability', 'fund_balance', 'revenue', 'expense']);
export const EntryKind = z.enum(['opening', 'contribution_call', 'payment', 'expense', 'manual', 'reversal']);

export const CreateFiscalYear = z
  .object({ label: z.string().trim().min(1).max(50), start: IsoDate, end: IsoDate })
  .refine((f) => f.start < f.end, { message: 'The fiscal year must end after it starts', path: ['end'] });
export type CreateFiscalYear = z.infer<typeof CreateFiscalYear>;

export const BudgetLineInput = z.object({ accountNumber: z.string().regex(/^\d{4}$/), fundCode: z.string(), amountCents: Cents });

export const SaveBudget = z.object({
  installments: z.int().min(1).max(12).default(12),
  lines: z.array(BudgetLineInput).min(1),
});
export type SaveBudget = z.infer<typeof SaveBudget>;

export const PostDueCalls = z.object({ asOf: IsoDate.optional() });

export const RecordPayment = z.object({
  unitId: Id,
  date: IsoDate,
  amountCents: Cents.refine((v) => v > 0, 'The amount must be positive'),
  reference: z.string().trim().max(100).optional(),
  /**
   * Fund whose bank account received the money (usually the operating account). Shares belonging to other
   * funds are then recorded as owed between funds (1900/2900) until transferred. Omitted: each fund's own account.
   */
  depositFundCode: z.string().optional(),
});
export type RecordPayment = z.infer<typeof RecordPayment>;

const LineInput = z
  .object({
    accountNumber: z.string().regex(/^\d{4}$/),
    fundCode: z.string(),
    unitId: Id.optional(),
    debitCents: Cents.default(0),
    creditCents: Cents.default(0),
    memo: z.string().max(200).optional(),
  })
  .refine((l) => (l.debitCents > 0) !== (l.creditCents > 0), 'Each line is either a debit or a credit');

export const PostEntry = z.object({
  date: IsoDate,
  kind: z.enum(['opening', 'expense', 'manual']).default('manual'),
  description: z.string().trim().min(1).max(300),
  reference: z.string().trim().max(100).optional(),
  lines: z.array(LineInput).min(2),
});
export type PostEntry = z.infer<typeof PostEntry>;

export const ReverseEntry = z.object({ date: IsoDate, reason: z.string().trim().min(1).max(300) });
export type ReverseEntry = z.infer<typeof ReverseEntry>;

export const AsOfQuery = z.object({ asOf: IsoDate.optional() });
export const JournalQuery = z.object({ limit: z.coerce.number().int().min(1).max(500).default(100) });

export interface Fund {
  id: string;
  code: string;
  kind: z.infer<typeof FundKind>;
  nameFr: string;
  nameEn: string;
}

export interface Account {
  id: string;
  number: string;
  type: z.infer<typeof AccountType>;
  nameFr: string;
  nameEn: string;
  allowedFundKinds: z.infer<typeof FundKind>[];
  role: 'bank' | 'receivable' | 'contribution' | 'fund_balance' | null;
  requiresUnit: boolean;
}

export interface FiscalYear {
  id: string;
  label: string;
  start: string;
  end: string;
  status: 'open' | 'closed';
}

export interface BudgetLine {
  accountNumber: string;
  fundCode: string;
  amountCents: number;
}

export interface Budget {
  id: string;
  fiscalYearId: string;
  status: 'draft' | 'adopted';
  installments: number;
  lines: BudgetLine[];
}

export interface ContributionCall {
  id: string;
  installment: number;
  dueDate: string;
  entryId: string | null;
}

export interface JournalLine {
  accountNumber: string;
  fundCode: string;
  unitId: string | null;
  debitCents: number;
  creditCents: number;
  memo: string | null;
}

export interface JournalEntry {
  id: string;
  date: string;
  kind: z.infer<typeof EntryKind>;
  description: string;
  reference: string | null;
  reversesEntryId: string | null;
  reversedByEntryId: string | null;
  lines: JournalLine[];
}

export interface StatementLine {
  entryId: string;
  date: string;
  kind: z.infer<typeof EntryKind>;
  description: string;
  chargeCents: number;
  paymentCents: number;
  balanceCents: number;
}

export interface UnitStatement {
  unitId: string;
  balanceCents: number;
  byFund: { fundCode: string; balanceCents: number }[];
  lines: StatementLine[];
}

export interface ArrearsRow {
  unitId: string;
  balanceCents: number;
  /** Overdue amounts by age, payments applied to the oldest charges first. */
  currentCents: number;
  days1to30Cents: number;
  days31to60Cents: number;
  days61to90Cents: number;
  over90Cents: number;
  /** Due date of the oldest charge not yet paid (payments settle the oldest first); null if nothing is owed. */
  oldestUnpaidDate: string | null;
}

export interface TrialBalanceRow {
  accountNumber: string;
  fundCode: string;
  debitCents: number;
  creditCents: number;
  /** Debit-positive for assets and expenses, credit-positive for the others. */
  balanceCents: number;
}

export interface FundSummary {
  fundCode: string;
  cashCents: number;
  receivableCents: number;
  /** Assets − liabilities: what the fund is worth. */
  netAssetsCents: number;
}

// --- Bank import and reconciliation

export * from './bank-csv';

export const ImportBankStatement = z.object({
  accountNumber: z.string().regex(/^\d{4}$/),
  fundCode: z.string(),
  fileName: z.string().trim().min(1).max(200),
  csv: z.string().min(1).max(2_000_000),
  mapping: BankCsvMapping,
});
export type ImportBankStatement = z.infer<typeof ImportBankStatement>;

export const BankTransactionsQuery = z.object({ status: z.enum(['unmatched', 'matched', 'ignored', 'all']).default('unmatched') });

export const MatchPayment = z.object({
  /** Units paid by this deposit; the amount is split between them by what each owes. */
  unitIds: z.array(Id).min(1),
});
export const MatchEntry = z.object({ entryId: Id });
export const PostFromTransaction = z.object({
  accountNumber: z.string().regex(/^\d{4}$/),
  description: z.string().trim().min(1).max(300).optional(),
});
export const IgnoreTransaction = z.object({ note: z.string().trim().min(1).max(300) });
export const ConfirmSuggestions = z.object({ transactionIds: z.array(Id).min(1).max(500) });

export interface BankImportResult {
  importId: string;
  imported: number;
  duplicates: number;
  from: string | null;
  to: string | null;
}

export type BankSuggestion =
  | {
      kind: 'payment';
      units: { unitId: string; amountCents: number }[];
      confidence: 'high' | 'medium';
      reasons: ('unit_label' | 'owner_name' | 'expected_amount' | 'balance_amount')[];
    }
  | { kind: 'entry'; entryId: string; date: string; description: string; confidence: 'high' | 'medium' };

export interface BankTransaction {
  id: string;
  accountNumber: string;
  fundCode: string;
  date: string;
  description: string;
  reference: string | null;
  amountCents: number;
  status: 'unmatched' | 'matched' | 'ignored';
  entryId: string | null;
  note: string | null;
  suggestion: BankSuggestion | null;
}

export interface BankProfile {
  accountNumber: string;
  fundCode: string;
  mapping: BankCsvMapping;
}
